import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '#environments';
import { PaginatedResult, ProjectSummary, ProjectDetail, CreateProject, UpdateProject, AddAssetsToProjectResponse } from '#models';
import { AuthService } from './auth.service';

export interface ProjectFilters {
    status?: string;
    vertical?: string;
    subvertical?: string;
    type?: string;
    page?: number;
    pageSize?: number;
}

@Injectable({ providedIn: 'root' })
export class ProjectService {
    private _http = inject(HttpClient);
    private _auth = inject(AuthService);
    private _base = `${environment.apiBaseUrl}/v1/projects`;

    getProjects(filters: ProjectFilters = {}): Observable<PaginatedResult<ProjectSummary>> {
        const params: Record<string, string> = {};
        if (filters.status) params['status'] = filters.status;
        if (filters.vertical) params['vertical'] = filters.vertical;
        if (filters.subvertical) params['subvertical'] = filters.subvertical;
        if (filters.type) params['type'] = filters.type;
        if (filters.page != null) params['page'] = String(filters.page);
        if (filters.pageSize != null) params['pageSize'] = String(filters.pageSize);
        return this._http.get<PaginatedResult<ProjectSummary>>(this._base, { params });
    }

    getProjectById(projectId: string): Observable<ProjectDetail> {
        return this._http.get<ProjectDetail>(`${this._base}/${projectId}`);
    }

    createProject(dto: CreateProject): Observable<ProjectSummary> {
        return this._http.post<ProjectSummary>(this._base, {
          ...dto,
          createdBy: this._auth.displayName()
        });
    }

    updateProject(projectId: string, dto: UpdateProject): Observable<ProjectSummary> {
        return this._http.put<ProjectSummary>(`${this._base}/${projectId}`, {
            ...dto,
          modifiedBy: this._auth.displayName()
        });
    }

    updateStatus(projectId: string, newStatus: string): Observable<ProjectSummary> {
        return this._http.patch<ProjectSummary>(`${this._base}/${projectId}/status`, {
            projectId,
            newStatus,
          modifiedBy: this._auth.displayName()
        });
    }

    deleteProject(projectId: string): Observable<void> {
        return this._http.delete<void>(`${this._base}/${projectId}`);
    }

    addAssets(projectId: string, assetIds: string[]): Observable<AddAssetsToProjectResponse> {
        return this._http.post<AddAssetsToProjectResponse>(
            `${this._base}/${projectId}/assets`, {
                assetIds,
            createdBy: this._auth.displayName()
            });
    }

    removeAsset(projectId: string, assetId: string): Observable<void> {
        return this._http.delete<void>(`${this._base}/${projectId}/assets/${assetId}`);
    }
}
